import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatCard } from "@/components/StatCard";
import { ClipboardList, Search, Download, QrCode, Users, CalendarDays, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const fmtScan = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ar", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return iso; }
};

export default function AttendanceLogs() {
  const [records, setRecords] = useState(null);
  const [q, setQ] = useState("");
  const [lecture, setLecture] = useState("all");

  useEffect(() => { api.get("/attendance/records").then((r) => setRecords(r.data)).catch(() => setRecords([])); }, []);

  const lectures = useMemo(() => Array.from(new Set((records || []).map((r) => r.lecture_title).filter(Boolean))).sort(), [records]);
  const stats = useMemo(() => {
    const recs = records || [];
    const today = new Date().toISOString().slice(0, 10);
    return {
      total: recs.length,
      students: new Set(recs.map((r) => r.student_id).filter(Boolean)).size,
      lectures: new Set(recs.map((r) => r.lecture_title).filter(Boolean)).size,
      today: recs.filter((r) => (r.timestamp || "").slice(0, 10) === today).length,
    };
  }, [records]);
  const filtered = useMemo(() => (records || []).filter((r) =>
    (lecture === "all" || r.lecture_title === lecture) &&
    (!q.trim() || (r.student_name || "").toLowerCase().includes(q.trim().toLowerCase()))
  ), [records, q, lecture]);

  const exportFile = async (format) => {
    try {
      const res = await api.get(`/attendance/logs/export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `attendance_logs.${format === "pdf" ? "pdf" : "xlsx"}`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("تعذّر التصدير"); }
  };

  if (records === null) return <PageLoader />;

  return (
    <>
      <PageHeader title="سجلات الحضور" subtitle="سجل مسح رموز QR للطلاب في المحاضرات"
        action={<div className="flex gap-2">
          <Button data-testid="export-logs-btn" onClick={() => exportFile("excel")} disabled={records.length === 0} className="rounded-xl bg-greenx hover:bg-greenx-light"><Download className="me-2 h-4 w-4" /> Excel</Button>
          <Button data-testid="export-logs-pdf-btn" onClick={() => exportFile("pdf")} disabled={records.length === 0} variant="outline" className="rounded-xl border-navy/20 text-navy"><Download className="me-2 h-4 w-4" /> PDF</Button>
        </div>} />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testid="stat-total" icon={QrCode} label="إجمالي عمليات المسح" value={stats.total} accent="navy" />
        <StatCard testid="stat-students" icon={Users} label="عدد الطلاب الحاضرين" value={stats.students} accent="green" />
        <StatCard testid="stat-lectures" icon={GraduationCap} label="عدد المحاضرات" value={stats.lectures} accent="blue" />
        <StatCard testid="stat-today" icon={CalendarDays} label="حضور اليوم" value={stats.today} accent="gold" />
      </div>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="logs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث باسم الطالب..." className="rounded-xl pr-9" />
        </div>
        <Select value={lecture} onValueChange={setLecture}>
          <SelectTrigger data-testid="logs-lecture-filter" className="w-full rounded-xl sm:w-64"><SelectValue placeholder="تصفية حسب المحاضرة" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">كل المحاضرات</SelectItem>
            {lectures.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {records.length === 0 ? (
        <EmptyState icon={QrCode} title="لا توجد سجلات حضور بعد" desc="ستظهر هنا سجلات الطلاب عند مسحهم لرمز QR الخاص بالمحاضرات." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-100 bg-card soft-shadow">
          <div className="overflow-x-auto">
            <table className="w-full text-right text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/70 text-xs font-semibold text-slate-500">
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">اسم الطالب</th>
                  <th className="px-4 py-3">اسم المحاضرة</th>
                  <th className="px-4 py-3">التاريخ</th>
                  <th className="px-4 py-3">وقت المسح الدقيق</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">لا توجد نتائج مطابقة</td></tr>
                ) : filtered.map((r, i) => (
                  <tr key={r.id || i} data-testid={`log-row-${i}`} className="border-b border-slate-50 transition-colors hover:bg-slate-50/60">
                    <td className="px-4 py-3 text-slate-400">{i + 1}</td>
                    <td className="px-4 py-3 font-semibold text-navy">{r.student_name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.lecture_title || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.date || "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{fmtScan(r.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2.5 text-xs text-slate-400">
            <span data-testid="logs-count">إجمالي السجلات المعروضة: {filtered.length} من {records.length}</span>
          </div>
        </div>
      )}
    </>
  );
}
